import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useDropzone } from 'react-dropzone';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';
import { useSoldLock } from '../SoldLock';

const BUCKET = 'skyway-dev-373d5.appspot.com';

// Exported so other tabs can compose their own set of customer-document slots
// (the Finance tab needs license front/back plus proof of insurance in one row).
export function LicenseSlot({ label, fieldId, customerId, data, disabled }) {
  const [loading, setLoading] = React.useState(false);
  // Sold cars still take buyer license and insurance uploads for title work,
  // but nobody but an admin removes one.
  const soldLocked = useSoldLock();
  const [fileLoaded, setFileLoaded] = React.useState(!!data[fieldId]);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [filepath, setFilepath] = React.useState(data[fieldId]);

  React.useEffect(() => {
    const hasFile = !!data[fieldId];
    setFileLoaded(hasFile);
    setFilepath(data[fieldId]);
    if (!hasFile) setPreviewUrl(null);
  }, [data, fieldId]);

  React.useEffect(() => {
    if (!filepath) return;
    firebase.app().storage(BUCKET).ref(filepath).getDownloadURL()
      .then(url => setPreviewUrl(url))
      .catch(() => setPreviewUrl(null));
  }, [filepath]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: 'image/*,.pdf',
    disabled: disabled || loading,
    onDrop: async (files) => {
      if (!files.length) return;
      const file = files[0];
      setLoading(true);
      const ext = file.name.split('.').pop();
      const filename = `customer_data/${customerId}-${fieldId}.${ext}`;
      const storageRef = firebase.app().storage(BUCKET).ref(filename);
      StateManager.setAlertAndOpen('Upload started — you can keep working.', 'info');
      StateManager.uploadFile({ storageRef, file }).then(async (result) => {
        if (result) {
          setFileLoaded(true);
          setFilepath(filename);
          await firebase.firestore().doc(`customers/${customerId}`).set({ [fieldId]: filename }, { merge: true });
        }
        setLoading(false);
      });
    },
  });

  const viewFile = async () => {
    const url = await firebase.app().storage(BUCKET).ref(filepath).getDownloadURL();
    window.open(url, '_blank');
  };

  const deleteFile = async () => {
    if (!window.confirm(`Remove this ${label.toLowerCase()}?`)) return;
    setLoading(true);
    try { await firebase.app().storage(BUCKET).ref(filepath).delete(); } catch (e) {}
    await firebase.firestore().doc(`customers/${customerId}`).set({ [fieldId]: null }, { merge: true });
    setFileLoaded(false);
    setFilepath(null);
    setPreviewUrl(null);
    setLoading(false);
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block' }}>
        {label}
      </Typography>

      {loading ? (
        <Box sx={{ height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
          <CircularProgress size={22} />
        </Box>
      ) : !fileLoaded ? (
        <Box
          {...getRootProps()}
          data-sold-ok
          sx={{
            height: 96,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            bgcolor: isDragActive ? (t) => alpha(t.palette.primary.main, 0.06) : 'grey.50',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
          }}
        >
          <input {...getInputProps()} />
          <CreditCardIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
            {isDragActive ? 'Drop here' : 'Drop or click'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', height: 96, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          {previewUrl ? (
            <img src={previewUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => alpha(t.palette.success.main, 0.08) }}>
              <CheckCircleIcon sx={{ fontSize: 28, color: 'success.main' }} />
            </Box>
          )}
          <Box sx={{
            position: 'absolute', inset: 0,
            bgcolor: 'rgba(0,0,0,0.48)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            opacity: 0, transition: 'opacity 0.15s',
            '&:hover': { opacity: 1 },
          }}>
            <IconButton size="small" onClick={viewFile} data-sold-ok sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.18)' }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {!disabled && !soldLocked && (
              <IconButton size="small" onClick={deleteFile} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.18)' }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function LicenseUpload({ customer = {}, showBack = true, disabled }) {
  if (!customer?.id) return null;

  return (
    <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1.5 }}>
      <LicenseSlot label="License Front" fieldId="license" customerId={customer.id} data={customer} disabled={disabled} />
      {showBack && (
        <LicenseSlot label="License Back" fieldId="license_back" customerId={customer.id} data={customer} disabled={disabled} />
      )}
    </Box>
  );
}
